import React from 'react'
import { Select, Input, Button, Space } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { ConditionNode, ValueType } from '../types'
import { ExpressionEditorConfig, findField, groupFields, operatorsForField } from '../fields'

const { Option, OptGroup } = Select

type Props = {
  node: ConditionNode
  config: ExpressionEditorConfig
  onChange: (node: ConditionNode) => void
  onRemove: () => void
}

export default function ExpressionCondition({ node, config, onChange, onRemove }: Props) {
  const field = findField(config.fields, node.field)
  const hasOptions = !!(field && field.options && field.options.length)
  const operators = operatorsForField(config, field)
  const fieldGroups = groupFields(config.fields)

  const handleFieldChange = (value: string) => {
    const next = findField(config.fields, value)
    const nextOptions = next && next.options ? next.options : []
    const nextOperators = operatorsForField(config, next)
    // When switching to a field with a curated value list, default to its first value.
    const keepValue = nextOptions.length === 0 || nextOptions.findIndex(v => v.value === node.value) !== -1
    // Keep the operator only if the new field still allows it.
    const keepOperator = nextOperators.some(o => o.value === node.operator)
    onChange({
      ...node,
      field: value,
      operator: keepOperator ? node.operator : (nextOperators[0] && nextOperators[0].value) || node.operator,
      value: keepValue ? node.value : nextOptions[0].value,
      valueType: nextOptions.length ? 'string' : node.valueType
    })
  }

  const handleValueChange = (value: string, valueType?: ValueType) => {
    onChange({ ...node, value, valueType: valueType || node.valueType })
  }

  return (
    <Space align='baseline' wrap>
      <Select
        showSearch
        style={{ minWidth: 220 }}
        value={node.field}
        placeholder='Field'
        optionFilterProp='children'
        onChange={handleFieldChange}
      >
        {fieldGroups.map(group => (
          <OptGroup key={group.group} label={group.group}>
            {group.fields.map(f => (
              <Option key={f.value} value={f.value}>
                {f.label}
              </Option>
            ))}
          </OptGroup>
        ))}
      </Select>

      <Select
        style={{ minWidth: 150 }}
        value={node.operator}
        onChange={(operator: string) => onChange({ ...node, operator })}
      >
        {operators.map(op => (
          <Option key={op.value} value={op.value}>
            {op.label}
          </Option>
        ))}
      </Select>

      {hasOptions ? (
        <Select
          showSearch
          style={{ minWidth: 160 }}
          value={node.value}
          placeholder='Value'
          onChange={(value: string) => handleValueChange(value, 'string')}
        >
          {field!.options!.map(opt => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      ) : (
        <Input
          style={{ minWidth: 160 }}
          value={node.value}
          placeholder='Value'
          onChange={e => handleValueChange(e.target.value)}
        />
      )}

      <Button
        type='text'
        danger
        icon={<DeleteOutlined />}
        onClick={onRemove}
        title='Remove condition'
      />
    </Space>
  )
}
